import React, { createContext, useReducer } from 'react';
import { AuthReducer, AuthenticationState, Types, AuthenticationAction } from './reducers';
import TokenService from './token-service';
import serviceApi from 'services/api';
import auth from 'services/auth';

const api = process.env.REACT_APP_ENVIRONMENT === 'local' ? auth : serviceApi;

interface Props {
  children: React.ReactNode;
}

const GetInitialStateValues = () => {
  const token = TokenService.getToken();
  const intialvalues = {
    user: {
      sub: '',
      role: '',
      uuid: '',
      error: '',
      isAuthenticated: false,
      isLoading: false,
      isError: false,
      status: ''
    }
  };

  try {
    if (Object.keys(token).length > 0) {
      const { sub, role, uuid } = TokenService.getDataFromJwtToken(token);
      if (sub || role || uuid) {
        return {
          user: {
            sub,
            role,
            uuid,
            error: '',
            isAuthenticated: true,
            isLoading: false,
            isError: false,
            status: ''
          }
        };
      }
    }
  } catch (err) {
    console.log(err);
  }

  return intialvalues;
};

const AuthContext = createContext<{
  state: AuthenticationState;
  login: (username: string, password: string) => void;
  logout: () => void;
}>({
  state: GetInitialStateValues(),
  login: () => null,
  logout: () => null
});

const mainReducer = (state: AuthenticationState, action: AuthenticationAction) =>
  AuthReducer(state, action);

const AuthProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(mainReducer, GetInitialStateValues());

  //Update state if changed in *ANOTHER* tab
  React.useEffect(() => {
    const token = TokenService.getToken();

    const updateAuthState = () => {
      const { sub, role, uuid } = TokenService.getDataFromJwtToken(token);
      if (sub || role || uuid) {
        dispatch({
          type: Types.Refresh,
          payload: {
            sub,
            role,
            uuid,
            isAuthenticated: true,
            isLoading: false,
            isError: false,
            status: 'success'
          }
        });
      }
    };

    if (Object.keys(token).length > 0) {
      updateAuthState();
    }

    window.addEventListener('storage', updateAuthState);

    return function cleanup() {
      window.removeEventListener('storage', updateAuthState);
    };
  }, []);

  const login = async (username: string, password: string) => {
    try {
      dispatch({
        type: Types.RequestLogin,
        payload: {
          isLoading: true,
          status: 'loading'
        }
      });
      return await api
        .post('/apiv1/admin/users/login', {
          username,
          password
        })
        .then((response) => {
          if (response.data.access) {
            TokenService.setToken(response.data);
            const { sub, uuid, role } = TokenService.getDataFromJwtToken(response.data);
            if (sub || role || uuid) {
              dispatch({
                type: Types.LoginSuccess,
                payload: {
                  sub: sub,
                  uuid: uuid,
                  role: role,
                  isLoading: false,
                  isError: false,
                  isAuthenticated: true,
                  status: 'success'
                }
              });
            }
            return { sub, uuid, role };
          } else {
            dispatch({
              type: Types.LoginError,
              payload: {
                error: response.data.message,
                isLoading: false,
                isError: true,
                status: 'error'
              }
            });
            return response.data.message;
          }
        });
    } catch (error) {
      dispatch({
        type: Types.LoginError,
        payload: {
          error: 'error',
          isLoading: false,
          isError: true,
          status: 'error'
        }
      });
      return error;
    }
  };

  const logout = async () => {
    dispatch({
      type: Types.LogOut
    });
    TokenService.removeUser();
  };

  const value = { state, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export { AuthProvider, useAuth };
